import { ResponsiveBar } from '@nivo/bar';
import { Box, LoadingIndicator, QueryErrorAlert } from 'components';
import { useCustomTranslation } from 'helpers/translationHelper';
import { useGetCompaniesCodebookQuery } from 'pages/admin/companies/queries';
import React, { useMemo } from 'react';

import { useDashboardOverviewQuery, useTopCompaniesQuery } from '../../queries';
import { buildCompanyNameMap, resolveCompanyName } from './dashboardHelpers';
import DashboardLineChart from './DashboardLineChart';
import DashboardRangeControls from './DashboardRangeControls';
import type { DashboardRange } from './useDashboardRange';

interface DispatchesTabProps {
  range: DashboardRange;
}

const CHART_HEIGHT = 340;

const DispatchesTab: React.FC<DispatchesTabProps> = ({ range }) => {
  const { tDashboard } = useCustomTranslation();
  const { from, to, granularity } = range;
  // Dispatch time series and per-company top list both come from the Statistics module. The top list
  // charts planned interventions, so it has to be ranked by that metric — not by core activity,
  // which also counts submitted forms.
  const overviewQuery = useDashboardOverviewQuery(from, to, granularity);
  const topQuery = useTopCompaniesQuery(from, to, 10, 'interventions');
  const companiesQuery = useGetCompaniesCodebookQuery();

  const lineData = useMemo(() => {
    if (!overviewQuery.data) return [];
    const series = overviewQuery.data.points;
    return [
      {
        id: tDashboard('dispatches_planned'),
        data: series.map((d) => ({ x: d.period, y: d.plannedInterventions }))
      },
      {
        id: tDashboard('dispatches_completed'),
        data: series.map((d) => ({ x: d.period, y: d.completedInterventions }))
      }
    ];
  }, [tDashboard, overviewQuery.data]);

  const topData = useMemo(() => {
    if (!topQuery.data) return [];
    const nameMap = buildCompanyNameMap(companiesQuery.data);
    // Reversed because a horizontal nivo bar chart draws the first entry at the bottom, which would
    // put the biggest company at the bottom of a "top companies" ranking.
    return topQuery.data
      .map((c) => ({
        company: resolveCompanyName(nameMap, c.companyId),
        count: c.interventions
      }))
      .reverse();
  }, [topQuery.data, companiesQuery.data]);

  const error = overviewQuery.error || topQuery.error || companiesQuery.error;
  const isLoading =
    overviewQuery.isFetching ||
    topQuery.isFetching ||
    companiesQuery.isFetching ||
    !overviewQuery.data ||
    !topQuery.data;

  return (
    <>
      <DashboardRangeControls {...range} />

      {error ? (
        <QueryErrorAlert
          error={error}
          onRetry={() => {
            overviewQuery.refetch();
            topQuery.refetch();
            companiesQuery.refetch();
          }}
        />
      ) : isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <Box>
            <h4>{tDashboard('dispatches_overTime')}</h4>
            <DashboardLineChart
              data={lineData}
              granularity={granularity}
              height={CHART_HEIGHT}
            />
          </Box>

          <div style={{ marginTop: 16 }}>
            <Box>
              <h4>{tDashboard('dispatches_topCompaniesPlanned')}</h4>
              <p>{tDashboard('dispatches_topCompaniesPlanned_hint')}</p>
              <div style={{ height: CHART_HEIGHT }}>
                <ResponsiveBar
                  data={topData}
                  keys={['count']}
                  indexBy="company"
                  layout="horizontal"
                  margin={{ top: 20, right: 20, bottom: 40, left: 160 }}
                  padding={0.3}
                  enableLabel
                  colors={{ scheme: 'nivo' }}
                  axisBottom={{ tickValues: 5 }}
                />
              </div>
            </Box>
          </div>
        </>
      )}
    </>
  );
};

export default DispatchesTab;
